/* Catalog view over the pinned rate version: groups, searchable entries, hourly variants (normal / skift / overtid), add rules. */
const VARIANT_RE = /\s+(skiftarbeid|skift|50\s*%\s*overtid|100\s*%\s*overtid)\s*$/i;
const EQUIP = /dokk|slipp|kran|kai|lift|truck|taubåt/i;
const COUNT_UNITS = new Set(["stk", "propell", "fat", "tømming", "punkt", "måling"]);
export const UNIT_SHORT = { time: "t", døgn: "døgn", m2: "m²", stk: "stk", kg: "kg", fat: "fat", liter: "l", kWh: "kWh", punkt: "pkt", måling: "måling", m: "m", propell: "propell", tømming: "tømming" };

export const VARIANTS = [
  { key: "normal", label: "Normal", hint: "Normal arbeidstid man–fre 07–22" },
  { key: "skift", label: "Skift", hint: "Skiftarbeid 15–22 – egen timesats, ikke i tillegg til normaltimer" },
  { key: "ot50", label: "OT 50 %", hint: "Full timesats for overtidstimene – ikke i tillegg til normaltimer" },
  { key: "ot100", label: "OT 100 %", hint: "Full timesats for overtidstimene – ikke i tillegg til normaltimer" },
];

function variantKey(desc) {
  const m = VARIANT_RE.exec(desc || "");
  if (!m) return "normal";
  const v = m[1].toLowerCase().replace(/\s/g, "");
  if (v.startsWith("skift")) return "skift";
  return v.startsWith("50") ? "ot50" : "ot100";
}

export function elKindFor(group, label) {
  if (group === "Timepriser") return EQUIP.test(label) ? "utstyr" : "arbeid";
  if (group === "Tillegg timesats") return "arbeid";
  if (EQUIP.test(group)) return "utstyr";
  return "annet";
}

/** Default quantity when something is added from the catalog: a minimum if the line has one, 1 for countable units, otherwise unknown. */
export function defaultQty(entry) {
  if (entry.min_qty) return entry.min_qty;
  if (COUNT_UNITS.has(entry.unit)) return 1;
  return null;
}

export function ruleNotes(entry, variant) {
  const n = [];
  if (variant && variant.key !== "normal") n.push(variant.hint);
  if (entry.group === "Tillegg timesats") n.push("Tillegg pr. time – kommer i tillegg til timesatsen for de samme timene");
  if (entry.billing === "etter_regning") n.push("Etter regning – ingen fast pris, posten blir stående uten sum");
  if (entry.billing === "pr_time") n.push("Faktureres pr. time etter timesatsene – timene legges som arbeid");
  if (entry.billing === "note" && entry.note) n.push(entry.note);
  if (entry.min_qty) n.push(`Minste mengde ${entry.min_qty} ${UNIT_SHORT[entry.min_unit] || entry.min_unit || ""}`.trim());
  if (entry.price == null && !["etter_regning", "note", "pr_time"].includes(entry.billing)) n.push("Pris mangler i prislisten");
  return n;
}

/** Build a flat list of catalog entries. Hourly lines that only differ by shift/overtime collapse into one entry with variants. */
export function buildCatalog(rates) {
  const lines = rates?.pricelist?.lines || [];
  const labor = rates?.labor_rates || [];
  const plStatus = rates?.pricelist?.provenance?.status;
  const rateByLine = new Map(labor.filter((r) => r.pricelist_line_id).map((r) => [r.pricelist_line_id, r]));
  const bindFor = (l) => { const r = rateByLine.get(l.id); return r ? { rate_id: r.id } : { pricelist_line_id: l.id }; };
  const entries = [];
  const byBase = new Map();
  lines.forEach((l) => {
    const base = { key: `p:${l.id}`, id: l.id, label: l.description, group: l.group, unit: l.unit, price: l.price, cost: rateByLine.get(l.id)?.cost ?? null, status: plStatus, billing: l.billing, min_qty: l.min_qty, min_unit: l.min_unit, note: l.note, bind: bindFor(l), elKind: elKindFor(l.group, l.description), variants: null };
    if (l.group === "Timepriser") {
      const name = l.description.replace(VARIANT_RE, "").trim();
      const vk = variantKey(l.description);
      const k = name.toLowerCase();
      if (!byBase.has(k)) {
        const e = { ...base, label: name, variants: [] };
        byBase.set(k, e);
        entries.push(e);
      }
      const e = byBase.get(k);
      const meta = VARIANTS.find((v) => v.key === vk);
      e.variants.push({ key: meta.key, short: meta.label, hint: meta.hint, lineId: l.id, label: l.description, price: l.price, bind: bindFor(l), cost: rateByLine.get(l.id)?.cost ?? null });
      if (vk === "normal") { e.price = l.price; e.bind = bindFor(l); e.id = l.id; e.key = `p:${l.id}`; }
    } else entries.push(base);
  });
  entries.forEach((e) => { if (e.variants) e.variants.sort((a, b) => VARIANTS.findIndex((v) => v.key === a.key) - VARIANTS.findIndex((v) => v.key === b.key)); });
  labor.filter((r) => !r.pricelist_line_id).forEach((r) => entries.push({ key: `r:${r.id}`, id: r.id, label: r.name, group: "Interne satser", unit: r.unit, price: r.price, cost: r.cost, status: r.provenance?.status, billing: "pris", bind: { rate_id: r.id }, elKind: r.kind || "arbeid", variants: null, department: r.department }));
  const groups = [];
  entries.forEach((e) => { let g = groups.find((x) => x.key === e.group); if (!g) { g = { key: e.group, label: e.group, count: 0 }; groups.push(g); } g.count += 1; });
  return { entries, groups, units: [...new Set(entries.map((e) => e.unit).filter(Boolean))] };
}

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/** Fast ranked search: every token must match label/group/unit; prefix hits rank first. */
export function searchCatalog(entries, q, { group, unit } = {}) {
  const tokens = norm(q).split(/\s+/).filter(Boolean);
  const out = [];
  entries.forEach((e) => {
    if (group && e.group !== group) return;
    if (unit && e.unit !== unit) return;
    const hay = norm(`${e.label} ${e.group} ${e.unit} ${(e.variants || []).map((v) => v.label).join(" ")}`);
    const label = norm(e.label);
    let score = 0;
    for (const t of tokens) {
      if (!hay.includes(t)) return;
      score += label.startsWith(t) ? 3 : label.includes(t) ? 2 : 1;
    }
    out.push([score, e]);
  });
  out.sort((a, b) => b[0] - a[0]);
  return out.map((x) => x[1]);
}

/** The request body for POST …/elements built from a catalog entry (and chosen hourly variant). Numbers come from the pinned version server-side. */
export function elementBody(entry, variant, rates) {
  const pick = variant || null;
  const qty = defaultQty(entry);
  const bind = pick ? pick.bind : entry.bind;
  return {
    kind: entry.elKind, description: pick ? pick.label : entry.label, unit: UNIT_SHORT[entry.unit] || entry.unit || null,
    qty: qty, qty_unknown: qty == null, ...bind,
    source: { type: bind.rate_id ? "sats" : "prisliste", version: rates?.version, group: entry.group },
  };
}
